import { useState } from "react";
import { Button, Card, Form, Input, Space, Select, Popconfirm } from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined, HolderOutlined } from "@ant-design/icons";
import "./report-body-editor.css";

const ReportBodyEditor = ({ value = [], onChange, disabled, onSaveMain }) => {
  const [editIndex, setEditIndex] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const typeOptions = [
    { value: "data", label: "SQL Запрос" },
    { value: "view", label: "HTML Дизайн" }
  ];

  const createNewItem = (type = "data") => ({ $type: type, name: "", sql: "", dataName: "", body: "" });

  const onAdd = () => {
    setEditIndex(-1);
    setEditItem(createNewItem());
  };

  const onEdit = (index) => {
    setEditIndex(index);
    setEditItem({ ...value[index] });
  };

  const onDelete = (index) => { onChange?.([...value.slice(0, index), ...value.slice(index + 1)]); };

  const onSave = () => {
    const newValue = editIndex === -1 ? [...value, editItem] : value.map((item, index) => index === editIndex ? editItem : item);    
    onChange?.(newValue);
        
    // Форма остается открытой для дальнейшего редактирования
    if (editIndex === -1) {
      // Для нового элемента обновляем editIndex на индекс добавленного элемента
      setEditIndex(newValue.length - 1);
    }
  };

  const onCancel = () => {
    setEditIndex(null);
    setEditItem(null);
  };

  const onTypeChange = (type) => {
    setEditItem(createNewItem(type));
  };

  const renderEditForm = () => {
    if (editIndex === null) return null;

    const isData = editItem.$type === "data";
    const isDisabled = !editItem.name || (isData && !editItem.sql) || (!isData && !editItem.body);

    return (<>
      <Card title={editIndex === -1 ? "Добавить элемент" : "Редактировать элемент"} className="report-body-editor-edit-form">
        <Form layout="vertical">
          <Form.Item label="Тип">
            <Select value={editItem.$type} onChange={onTypeChange} options={typeOptions} disabled={editIndex !== -1}/>
          </Form.Item>
          <Form.Item label="Наименование" required>
            <Input value={editItem.name} onChange={(e) => setEditItem({ ...editItem, name: e.target.value })} maxLength={200} />
          </Form.Item>
          {isData ? (
            <Form.Item label="SQL Запрос" required>
              <Input.TextArea value={editItem.sql} onChange={(e) => setEditItem({ ...editItem, sql: e.target.value })} rows={6} placeholder="SELECT * FROM ..." />
            </Form.Item>
          ) : (
            <>
              <Form.Item label="Имя данных">
                <Input
                  value={editItem.dataName}
                  onChange={(e) => setEditItem({ ...editItem, dataName: e.target.value })}
                  maxLength={200}
                  placeholder="Имя источника данных"
                />
              </Form.Item>
              <Form.Item label="HTML Body" required>
                <Input.TextArea value={editItem.body} onChange={(e) => setEditItem({ ...editItem, body: e.target.value })} rows={8} placeholder="<div>...</div>" />
              </Form.Item>
            </>
          )}
          <Form.Item>
            <Space>
              <Button type="primary" onClick={onSave} disabled={isDisabled}>Сохранить</Button>
              <Button onClick={onCancel}>Закрыть</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card></>
    );
  };

  const getTypeLabel = (type) => {
    return typeOptions.find(t => t.value === type)?.label || type;
  };

  const dragHandlers = {
    start: (index) => (e) => {
      setDragIndex(index);
      e.dataTransfer.effectAllowed = 'move';
    },
    over: (index) => (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dragIndex !== null && dragIndex !== index) {
        setDragOverIndex(index);
      }
    },
    leave: () => setDragOverIndex(null),
    drop: (dropIndex) => (e) => {
      e.preventDefault();
      if (dragIndex === null || dragIndex === dropIndex) {
        setDragIndex(null);
        setDragOverIndex(null);
        return;
      }

      const newValue = [...value];
      const draggedItem = newValue[dragIndex];
      newValue.splice(dragIndex, 1);
      newValue.splice(dropIndex, 0, draggedItem);
      
      onChange?.(newValue);
      setDragIndex(null);
      setDragOverIndex(null);
    },
    end: () => {
      setDragIndex(null);
      setDragOverIndex(null);
    }
  };

  return (
    <div>
      <Button onClick={() => onSaveMain()}>Сохранить</Button>
      {editIndex === null && (<Button type="dashed" onClick={onAdd} icon={<PlusOutlined />} className="report-body-editor-add-button" disabled={disabled}>Добавить элемент</Button>)}
      {renderEditForm()}
      <div className="report-body-editor-items-container">
        {value.map((item, index) => (
          <Card
            key={index}
            size="small"
            className={`draggable-card ${dragIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
            draggable={editIndex === null}
            onDragStart={dragHandlers.start(index)}
            onDragOver={dragHandlers.over(index)}
            onDragLeave={dragHandlers.leave}
            onDrop={dragHandlers.drop(index)}
            onDragEnd={dragHandlers.end}
            title={
              <Space>
                {editIndex === null && <HolderOutlined className="report-body-editor-drag-icon" />}
                <span className="report-body-editor-item-title">{item.name || "(без названия)"}</span>
                <span className="report-body-editor-item-type">({getTypeLabel(item.$type)})</span>
              </Space>
            }
            extra={
              editIndex === null && (
                <Space>
                  <EditOutlined onClick={() => onEdit(index)} className="report-body-editor-edit-icon" />
                  <Popconfirm title="Удалить элемент?" onConfirm={() => onDelete(index)} okText="Да" cancelText="Нет">
                    <DeleteOutlined className="report-body-editor-delete-icon" />
                  </Popconfirm>
                </Space>
              )
            }
          />
        ))}
      </div>

      {value.length === 0 && editIndex === null && (
        <div className="report-body-editor-empty-state">Список элементов пуст</div>
      )}
    </div>
  );
};

export default ReportBodyEditor;